import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/revenda-iptv/como-ser")({
  head: () => ({
    meta: [
      { title: "Como Ser um Revendedor IPTV de Sucesso 2026 | P2 PRO" },
      { name: "description", content: "Guia definitivo: aprenda como ser um revendedor IPTV lucrativo. Passo a passo para iniciar seu negócio, conquistar clientes e escalar seus lucros no modelo pós-pago." },
      { name: "keywords", content: "como ser revendedor iptv, como começar revenda iptv, passo a passo revenda iptv, lucrar com iptv" },
    ],
  }),
  component: () => (
    <SEOPage
      variant="split"
      category="Carreira & Negócios"
      title="COMO SER UM REVENDEDOR IPTV DE SUCESSO EM 2026"
      subtitle="O caminho profissional para sair do zero e construir um faturamento mensal de 5 dígitos no mercado de streaming."
      introParagraphs={[
        "Ingressar no mercado de Revenda IPTV é, sem dúvida, uma das oportunidades de negócio mais democráticas e rentáveis da atualidade. Com o avanço da internet de alta velocidade e a mudança no comportamento de consumo de TV, o streaming tornou-se o produto mais desejado pelas famílias brasileiras. No entanto, ser apenas 'mais um' vendedor não é o suficiente para quem busca liberdade financeira real.",
        "Para se tornar um revendedor de elite, você precisa de três pilares: um produto que não trava (estabilidade), um modelo financeiro inteligente e uma estrutura de <a href='/' className='text-electric hover:underline font-bold'>Painel IPTV Pós Pago</a> validada. Na P2 PRO, nós fornecemos os três. Nosso modelo permite que você inicie seu negócio com investimento inicial baixíssimo, focando 100% do seu esforço na aquisição de clientes enquanto nós cuidamos de toda a engenharia pesada nos bastidores.",
        "Neste guia exaustivo, vamos percorrer cada etapa da sua jornada, desde a mentalidade empreendedora necessária até as técnicas avançadas de fechamento via WhatsApp. Prepare-se para desvendar os segredos que os grandes players do mercado utilizam para manter taxas de renovação acima de 90% e crescer de forma orgânica e imparável."
      ]}
      sections={[
        {
          title: "O Mindset de um Distribuidor Master",
          type: "featured",
          content: (
            <div className="grid lg:grid-cols-2 gap-12 items-center text-white">
              <div>
                <h3 className="text-2xl font-black mb-6 italic uppercase">Não venda apenas canais, venda experiência</h3>
                <p className="text-white/80 leading-relaxed mb-8 text-sm">
                  O erro do iniciante é focar apenas no preço. O revendedor de sucesso foca na entrega. Ao utilizar a infraestrutura P2 PRO, você entrega estabilidade 4K nativa, um EPG que funciona de verdade e um catálogo de filmes que atualiza diariamente. Isso transforma um cliente comum em um fã da sua marca que te indicará para toda a rede de amigos dele.
                </p>
                <div className="space-y-4">
                  {[
                    "Foco total na satisfação do cliente final",
                    "Uso estratégico de testes gratuitos de 6h",
                    "Gestão financeira rigorosa via Painel Master",
                    "Construção de autoridade nas redes sociais"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/10">
                      <CheckCircle2 className="w-5 h-5 text-electric" />
                      <span className="font-bold text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-electric/10 border border-electric/20 p-8 rounded-[3rem] text-center">
                <div className="text-5xl mb-4">🏆</div>
                <h4 className="text-xl font-black mb-2 italic">A MÁGICA DA RECORRÊNCIA</h4>
                <p className="text-white/60 text-sm leading-relaxed">
                  Diferente de uma loja de roupas que precisa vender um produto novo todo dia, no IPTV você vende uma vez e recebe todos os meses. Com apenas 100 clientes ativos, você já garante uma base financeira sólida e vitalícia.
                </p>
              </div>
            </div>
          )
        },
        {
          title: "Etapas Técnicas para o Início Profissional",
          type: "grid",
          content: (
            <>
              {[
                { title: "Domínio do Painel", desc: "Aprenda a criar usuários, renovar planos e monitorar conexões em tempo real com nossa interface intuitiva.", icon: "🛠️" },
                { title: "Estratégia de DNS", desc: "Utilize nossos links M3U e DNS personalizados para garantir que seu sinal nunca seja bloqueado por operadoras.", icon: "🌐" },
                { title: "Configuração de Apps", desc: "Domine a instalação nos principais aplicativos do mercado: OneX, P2 Play, Smarters e IBO Player.", icon: "📲" },
                { title: "Atendimento VIP", desc: "Aprenda a utilizar scripts de vendas que quebram objeções e fecham negócios em minutos.", icon: "💬" }
              ].map((item, i) => (
                <div key={i} className="p-8 rounded-3xl bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all">
                  <div className="text-3xl mb-4">{item.icon}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </>
          )
        }
      ]}
      benefits={[
        { title: "Independência Total", description: "Seja seu próprio chefe e defina seus horários de trabalho." },
        { title: "Escalabilidade Infinita", description: "O mesmo esforço para gerenciar 10 clientes serve para gerenciar 500." },
        { title: "Apoio de Engenharia", description: "Conte com nosso time técnico monitorando os sinais 24h por dia." }
      ]}
      howItWorks={[
        { title: "Cadastro", description: "Receba seus acessos e explore as funcionalidades do painel gerencial." },
        { title: "Testes", description: "Libere demonstrações gratuitas para comprovar a qualidade do sinal." },
        { title: "Venda", description: "Receba o pagamento direto do cliente e ative a linha no painel." },
        { title: "Suporte", description: "Mantenha o relacionamento e garanta a renovação mensal automática." }
      ]}
      diferenciais={[
        { title: "Sem Estoque de Créditos", description: "No modelo pós-pago, você não empata capital em créditos parados." },
        { title: "Servidores Ultra-Rápidos", description: "Hospedagem Tier 3 para garantir latência zero em horários de pico." },
        { title: "App Exclusivo P2 PRO", description: "Aplicativo próprio leve e compatível com todas as Smart TVs." }
      ]}
      faq={[
        { question: "Preciso ter experiência anterior?", answer: "Não. Fornecemos todo o treinamento e scripts de vendas necessários." },
        { question: "Quanto tempo até minha primeira venda?", answer: "Muitos dos nossos parceiros realizam vendas nas primeiras 24 horas de ativação." },
        { question: "O painel funciona em iPhone?", answer: "Sim, nossa interface é totalmente responsiva para qualquer dispositivo." }
      ]}
      breadcrumbs={[
        { label: "Revenda IPTV", href: "/revenda-iptv" },
        { label: "Como Ser", href: "/revenda-iptv/como-ser" }
      ]}
      relatedArticles={[
        { title: "Quanto Ganha um Revendedor", href: "/revenda-iptv/quanto-ganha" },
        { title: "Revenda Pós-Pago", href: "/revenda-iptv/pos-pago" },
        { title: "Planos de Revenda", href: "/revenda-iptv/plano" }
      ]}
    />
  ),
});
